import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { Donor } from '../interfaces/donor';
import { Recipient } from '../interfaces/recipient';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, throwError, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { FilteredParticipant, Participant } from '../interfaces/participant';
import { Approval } from '../interfaces/approval';

@Injectable({
  providedIn: 'root'
})
export class ParticipantService {
  private apiUrl = environment.apiUrl + 'participants';

  constructor(private http: HttpClient) {}

  createDonor(donorData: Donor): Observable<Donor> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<Donor>(this.apiUrl + '/donor', donorData, { headers }).pipe(
      catchError((error) => {
        console.error('Error creating donor:', error);
        return throwError(() => new Error('Failed to create donor'));
      })
    );
  }

  createRecipient(recipientData: Recipient): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.http.post<Recipient>(this.apiUrl + '/recipient', recipientData, { headers }).pipe(
      catchError((error) => {
        console.error('Error creating recipient:', error);
        return throwError(() => new Error('Failed to create recipient'));
      })
    );
  }

  importRecipients(recipientData: Recipient[]): Observable<Recipient[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Recipient[]>(this.apiUrl + '/recipients', recipientData, { headers }).pipe(
      catchError((error) => {
        console.error('Error importing recipients:', error);
        return throwError(() => new Error('Failed to import recipients'));
      })
    );
  }

  importDonors(donorData: Donor[]): Observable<Donor[]> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Donor[]>(this.apiUrl + '/donors', donorData, { headers }).pipe(
      catchError((error) => {
        console.error('Error importing donors:', error);
        return throwError(() => new Error('Failed to import donors'));
      })
    );
  }

  getParticipantCount(): Observable<number> {
    return this.http.get<{ totalParticipants: number }>(this.apiUrl + '/count').pipe(map((response) => response.totalParticipants));
  }

  getAllParticipants(): Observable<Participant[]> {
    return this.http.get<Participant[]>(this.apiUrl).pipe(
      catchError((error) => {
        console.error('Error fetching participants:', error);
        return throwError(() => new Error('Failed to fetch participants'));
      })
    );
  }

  getAllApprovals(): Observable<Approval[]> {
    return this.http.get<Approval[]>(this.apiUrl + '/approvals').pipe(
      catchError((error) => {
        console.error('Error fetching approvals:', error);
        return throwError(() => new Error('Failed to fetch approvals'));
      })
    );
  }

  createApprovalRequest(approvalData: Approval): Observable<Approval> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    console.log(approvalData);
    return this.http.post<Approval>(this.apiUrl + '/approvals', approvalData, { headers }).pipe(
      catchError((error) => {
        console.error('Error creating approval:', error);
        return throwError(() => new Error('Failed to create approval'));
      })
    );
  }

  createApprovalRequestForPrivileges(approvalData: Approval): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    console.log(approvalData);
    return this.http.post<Recipient>(this.apiUrl + '/approvals/privileges', approvalData, { headers }).pipe(
      catchError((error) => {
        console.error('Error creating approval:', error);
        return throwError(() => new Error('Failed to create approval'));
      })
    );
  }

  updateApproval(approvalData: Approval): Observable<Approval> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .patch<Approval>(`${this.apiUrl}/approvals/${approvalData.id}`, approvalData, {
        headers
      })
      .pipe(
        catchError((error) => {
          console.error('Error updating approval:', error);
          return throwError(() => new Error('Failed to update approval'));
        })
      );
  }

  deleteApproval(approvalId: string): Observable<{ message: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .delete<{ message: string }>(`${this.apiUrl}/approvals/${approvalId}`, { headers })
      .pipe(
        catchError((error) => {
          const errorMessage = error.error?.message || 'Failed to delete approval. Please try again.';
          console.error('Error deleting approval:', errorMessage);
          return throwError(() => new Error(errorMessage));
        })
      );
  }

  getFilteredParticipants(filter: string = '', offset: string = '0', limit: string = '50', warnings: boolean | null = null, priorities: boolean | null = null): Observable<FilteredParticipant> {
    return this.http
      .get<FilteredParticipant>(`${this.apiUrl}/filtered`, {
        params: {
          filter: filter || '',
          offset: offset,
          limit: limit,
          // ! - Used to make typescript see the result as a FilterParticipant object and not an ArrayBuffer.
          warning: warnings!,
          priority: priorities!
        },
        responseType: 'json' // Ensure the response is treated as JSON
      })
      .pipe(
        catchError((error) => {
          console.error('Error fetching participants:', error);
          return throwError(() => new Error('Failed to fetch participants'));
        })
      );
  }

  updateDonor(donorData: Donor): Observable<Donor> {
    console.log(donorData);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .patch<Donor>(`${this.apiUrl}/donor/${donorData.id}`, donorData, {
        headers
      })
      .pipe(
        catchError((error) => {
          console.error('Error updating donor:', error);
          return throwError(() => new Error('Failed to update donor'));
        })
      );
  }

  getDonor(donorId: string): Observable<Donor> {
    return this.http.get<Donor>(`${this.apiUrl}/donor/${donorId}`).pipe(
      catchError((error) => {
        console.error('Error getting donor:', error);
        return throwError(() => new Error('Failed to get donor'));
      })
    );
  }

  getRecipient(recipientId: string): Observable<Recipient> {
    return this.http.get<Recipient>(`${this.apiUrl}/recipient/${recipientId}`).pipe(
      catchError((error) => {
        console.error('Error getting recipient:', error);
        return throwError(() => new Error('Failed to get recipient'));
      })
    );
  }

  updateRecipient(recipientData: Recipient): Observable<Recipient> {
    console.log(recipientData);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http
      .patch<Donor>(`${this.apiUrl}/recipient/${recipientData.id}`, recipientData, {
        headers
      })
      .pipe(
        catchError((error) => {
          console.error('Error updating recipient:', error);
          return throwError(() => new Error('Failed to update recipient'));
        })
      );
  }

  deleteDonor(donorId: string, reason: string): Observable<Donor> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete<Donor>(`${this.apiUrl}/donor/${donorId}`, { 
      headers,
      body: { reason }
    }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to delete donor. Please try again.';
        console.error('Error deleting donor:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  deleteRecipient(recipientId: string, reason: string): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.delete<Recipient>(`${this.apiUrl}/recipient/${recipientId}`, {
      headers,
      body: { reason }
    }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to delete recipient. Please try again.';
        console.error('Error deleting recipient:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  dismissMultipleDonorWarning(recipientId: string, reason: string): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Recipient>(`${this.apiUrl}/recipient/${recipientId}/dismiss-multiple-donor-warning`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to dismiss warning. Please try again.';
        console.error('Error dismissing multiple donor warning:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  dismissAcceptableBloodTypesWarning(recipientId: string, reason: string): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Recipient>(`${this.apiUrl}/recipient/${recipientId}/dismiss-acceptable-bloodtypes-warning`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to dismiss warning. Please try again.';
        console.error('Error dismissing acceptable blood types warning:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  dismissMaxDonorAgeWarning(recipientId: string, reason: string): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Recipient>(`${this.apiUrl}/recipient/${recipientId}/dismiss-max-donor-age-warning`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to dismiss warning. Please try again.';
        console.error('Error dismissing max donor age warning:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  dismissMaxMismatchesWarning(recipientId: string, reason: string): Observable<Recipient> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<Recipient>(`${this.apiUrl}/recipient/${recipientId}/dismiss-max-mismatches-warning`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.message || 'Failed to dismiss warning. Please try again.';
        console.error('Error dismissing max mismatches warning:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  toggleMinimatchStatus(): Observable<{
    message: string;
    activated?: {
      pairs: number;
      altruists: number;
      standaloneDonors: number;
      standaloneRecipients: number;
    };
    deactivated?: {
      pairs: number;
      altruists: number;
      standaloneDonors: number;
      standaloneRecipients: number;
    };
  }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(`${this.apiUrl}/toggle-minimatch-status`, {}, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to toggle minimatch status. Please try again.';
        console.error('Error toggling minimatch status:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  setMinimatchInactive(allCrossover: boolean, pmGroups: string[]): Observable<{
    message: string;
    deactivated?: {
      pairs: number;
      altruists: number;
      standaloneDonors: number;
      standaloneRecipients: number;
    };
  }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(`${this.apiUrl}/set-minimatch-inactive`, {
      allCrossover,
      pmGroups
    }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to set minimatch inactive status. Please try again.';
        console.error('Error setting minimatch inactive:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  toggleMinimatchInactiveToActive(): Observable<{
    message: string;
    activated?: {
      pairs: number;
      altruists: number;
      standaloneDonors: number;
      standaloneRecipients: number;
    };
  }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<any>(`${this.apiUrl}/toggle-minimatch-inactive-to-active`, {}, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to activate minimatch participants. Please try again.';
        console.error('Error toggling minimatch inactive to active:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  deactivateRecipientWithPairs(recipientId: string, reason: string): Observable<{ message: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ message: string }>(`${this.apiUrl}/recipient/${recipientId}/deactivate-with-pairs`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to deactivate recipient with pairs. Please try again.';
        console.error('Error deactivating recipient with pairs:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  activateRecipientWithPairs(recipientId: string): Observable<{ message: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ message: string }>(`${this.apiUrl}/recipient/${recipientId}/activate-with-pairs`, {}, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to activate recipient with pairs. Please try again.';
        console.error('Error activating recipient with pairs:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  changeRecipientInactiveReasonWithPairs(recipientId: string, reason: string): Observable<{ message: string }> {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
    return this.http.post<{ message: string }>(`${this.apiUrl}/recipient/${recipientId}/change-reason-with-pairs`, { reason }, { headers }).pipe(
      catchError((error) => {
        const errorMessage = error.error?.error || 'Failed to change inactive reason for group. Please try again.';
        console.error('Error changing inactive reason for recipient with pairs:', errorMessage);
        return throwError(() => new Error(errorMessage));
      })
    );
  }
}
